import React, { useState } from "react";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CircularProgress from "@mui/material/CircularProgress";

import { toast } from "react-toastify";
import Constant from "../../Constant";
import postService from "../../Services/post-service";

function ListingCard({
  id,
  title,
  location,
  type,
  dateAdded,
  description,
  percentageSold = 0,
  date,
  unitsLeft = 0,
  pricePerUnit = 0,
  areaCovered,
  developerName,
  price = 0,
  image,
  isSaved = false,
  onSaveToggle,
  onClick,
}) {
  const [loading, setLoading] = useState(false);
  // BOOKMARK HANDLER
  const handleBookmarkClick = async (e) => {
    e.stopPropagation();

    if (loading) return;

    setLoading(true);

    try {
      const response = isSaved
        ? await postService.removeProjectBuyLater(id)
        : await postService.addProjectBuyLater(id);

      if (response.data?.statusCode === 200) {
        onSaveToggle?.(id, !isSaved);

        toast.success(response.data.description || "Bookmark updated", {
          autoClose: 1000,
        });
      } else {
        toast.error(response.data?.description || "Unable to update bookmark", {
          autoClose: 1000,
        });
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.description || "Unable to update bookmark",
        { autoClose: 1000 },
      );
    } finally {
      setLoading(false);
    }
  };

  const isLand = type === "LAND_LISTING" || type === "Land";

  const displayBadgeText = isLand ? "Land" : "Property";

  const soldPercentage = Math.min(
    100,
    Math.max(0, Number(percentageSold) || 0),
  );

  return (
    <div
      onClick={onClick}
      onKeyDown={(e) => {
        if (
          e.target === e.currentTarget &&
          (e.key === "Enter" || e.key === " ")
        ) {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`View ${title} listing`}
      className="bg-white rounded-2xl border border-[#E5E7EB] p-4 mb-4 hover:shadow-[#00BC67] transition-all duration-200 cursor-pointer flex flex-col md:flex-row gap-5 items-start"
    >
      <div className="w-full md:w-[270px] lg:w-[290px] h-[180px] md:h-[185px] rounded-xl overflow-hidden relative flex-shrink-0 bg-gray-100">
        <img src={image} alt={title} className="w-full h-full object-cover" />

        <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5 z-10">
          <button
            type="button"
            onClick={handleBookmarkClick}
            disabled={loading}
            aria-label={isSaved ? "Remove bookmark" : "Save listing"}
            aria-pressed={isSaved}
            className="w-7 h-7 rounded-md bg-white/90 backdrop-blur-xs flex items-center justify-center shadow-xs text-gray-700 hover:text-green-600 transition disabled:cursor-not-allowed"
          >
            {loading ? (
              <CircularProgress
                size={14}
                thickness={5}
                sx={{ color: "#00BC67" }}
              />
            ) : isSaved ? (
              <BookmarkIcon
                sx={{
                  fontSize: 17,
                  color: "#00BC67",
                }}
              />
            ) : (
              <BookmarkBorderIcon sx={{ fontSize: 17 }} />
            )}
          </button>

          {/* Listing Type */}
          <span
            className={`px-3 py-0.5 rounded-[6px] text-center text-[10px] font-semibold text-white shadow-xs ${
              isLand ? "bg-[#76D400]" : "bg-[#0088FF]"
            }`}
          >
            {displayBadgeText}
          </span>
        </div>
      </div>
      <div className="flex-1 flex flex-col justify-between min-w-0 min-h-[180px] w-full">
        <div>
          <div className="flex justify-between items-start gap-2">
            <h3 className="text-[#000000] font-[700] text-[20px] leading-tight">
              {title}
            </h3>

            <div className="flex items-center gap-1 text-[#808285] text-[11px] flex-shrink-0">
              <CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />

              <span>Added on {dateAdded}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 text-[#999FA9] text-[12px] mt-1">
            <PlaceOutlinedIcon
              sx={{
                fontSize: 14,
                color: "#999FA9",
              }}
            />

            <span>{location}</span>
          </div>
          <p className="text-[#787F8D] font-[700] text-[12px] leading-relaxed line-clamp-2 mt-1.5">
            {description}
          </p>
        </div>
        <div className="mt-3">
          <div className="mb-2.5">
            <p className="text-[11px] font-semibold text-[#1E222B] mb-1">
              {soldPercentage}% sold
            </p>

            <div className="w-full max-w-[320px] h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#00BC67] rounded-full transition-all duration-500"
                style={{
                  width: `${soldPercentage}%`,
                }}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 pt-1 border-t border-gray-100 sm:border-0">
            <div className="flex items-center gap-x-4 gap-y-1 text-[11px] text-[#787F8D] font-[700] flex-wrap">
              {date && <span>{date}</span>}

              <span>{unitsLeft} unit left</span>

              <span>{Constant.FormatNumber(pricePerUnit)}/unit</span>

              {areaCovered && <span>{areaCovered}</span>}

              {developerName && <span>{developerName}</span>}
            </div>

            {/* Price */}
            <div className="text-left sm:text-right flex-shrink-0">
              <span className="text-[#999FA9] font-[400] text-[14px] block leading-none mb-0.5">
                Price
              </span>

              <span className="text-[#1E222B] font-[700] lg:text-[22px] tracking-tight">
                ₦ {Constant.FormatNumber(price)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListingCard;
